import React, { useState } from 'react';
import { useParams } from 'react-router';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@mui/styles';
import {
  CardHeader,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import API from 'common/api';
import useConnectErrorHandler from 'helpers/errorHandlers';
import { PlaceHolderText } from 'components/PlaceholderCard/PlaceholderCard';
import Progress from 'components/Progress/Progress';
import { delWithBody } from 'helpers/query/fetchers';
import { useSnackbar } from 'notistack';
import SnackMessage from 'components/SnackMessage/SnackMessage';
import useUser from 'hooks/useUser';
import {
  Card,
  Typography
} from 'bso-ui';
import { isAppAdmin } from 'helpers/permissions';
import InterfaceConfigurationCreate from './Form/InterfaceConfigurationCreate';
import ConfigureActionButtons from './ConfigureActionButtons';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2)
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  actions: {
    justifyContent: 'flex-end'
  },
  tableCell: {
    padding: '0px 16px'
  },
  tableHeader: {
    backgroundColor: '#F8FAFE'
  }
}));

const InterfaceDecompressions = ({ decompression, uncompressedMac, refetch = null }) => {
  const { iid } = useParams();
  const classes = useStyles();
  const minHeight = '20vh';
  const height = '40vh';
  const [{ data: userData }] = useUser();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { handleError } = useConnectErrorHandler();

  const noData = () => (
    <TableBody>
      <TableRow>
        <TableCell align="center" colSpan={4}>
          <Typography color="primary" variant="caption">NO DATA</Typography>
        </TableCell>
      </TableRow>
    </TableBody>
  );

  const deleteDecompression = (item) => {
    setLoading(true);
    const url = `${API.INTERFACE_COMPRESSION_DYNAMIC(iid)}/del/`;
    const payload = { ...item, type: 'decompression' };
    delWithBody(url, payload)
      .then(() => {
        const msg = `${payload.type} ID: ${payload.id} has been removed from the switch.`;
        refetch();

        enqueueSnackbar(msg, {
          content: (key) => <SnackMessage id={key} message={msg} variant="success" />
        });
      })
      .catch((error) => {
        setLoading(false);
        handleError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderData = () => (
    <Grid container spacing={4} alignItems="stretch">
      <Grid item lg={12} xs={12}>
        <Card style={{ minHeight }}>
          <CardHeader
            disableTypography
            title={<Typography variant="h4">Decompression</Typography>}
          />
          <PerfectScrollbar>
            <Table size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle1">ID</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle1">MAC source</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle1">MAC destination</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle1">Action</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {decompression.map((x) => (
                  <TableRow
                    hover
                    key={`decompression${x.id}`}
                  >
                    <TableCell className={classes.tableCell}>
                      <Typography color="primary" variant="body2" component="span">
                        {x.id}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography color="primary" variant="body2">
                        {x.mac_src || '---'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography color="primary" variant="body2">
                        {x.mac_dst || '---'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <ConfigureActionButtons
                        type="decompression"
                        compression={decompression}
                        item={x}
                        handleDelete={deleteDecompression}
                        refetch={refetch}
                        userData={userData}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {decompression.length < 16 && (
                <TableRow
                  hover
                  key={`decompression${decompression.length + 1}`}
                >
                  <TableCell className={classes.tableCell}>
                    <Typography color="primary" variant="body2" component="span">
                      {decompression.length + 1}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography color="primary" variant="body2">
                      ---
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography color="primary" variant="body2">
                      ---
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <ConfigureActionButtons
                      type="decompression"
                      compression={decompression}
                      handleDelete={deleteDecompression}
                      refetch={refetch}
                      userData={userData}
                    />
                  </TableCell>
                </TableRow>
                )}
              </TableBody>
            </Table>
          </PerfectScrollbar>
        </Card>
      </Grid>
      <Grid item lg={12} xs={12}>
        <Card style={{ minHeight }}>
          <CardHeader
            disableTypography
            title={<Typography variant="h4">Uncompressed MAC</Typography>}
          />
          <PerfectScrollbar>
            <Table size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle1">MAC source</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle1">MAC destination</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle1">Action</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              {uncompressedMac && uncompressedMac.length > 0
                ? (
                  <TableBody>
                    {uncompressedMac.map((item, index) => (
                      <TableRow
                        hover
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                      >
                        <TableCell className={classes.tableCell}>
                          <Typography color="primary" variant="body2">{item.mac_src}</Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Typography color="primary" variant="body2">{item.mac_dst}</Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {(userData.is_staff || isAppAdmin(userData.groups)) ? (
                            <InterfaceConfigurationCreate
                              title="Add to Decompression"
                              button={{
                                content: 'Add',
                                color: 'primary',
                                variant: 'standard',
                                state: 'outlined',
                                disabled: decompression.length >= 16,
                                customSize: true
                              }}
                              data={{ ...item, type: 'decompression' }}
                              prefilledId={decompression ? decompression.length + 1 : 1}
                              refetch={refetch}
                            />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )
                : noData()}
            </Table>
          </PerfectScrollbar>
        </Card>
      </Grid>
    </Grid>
  );

  if (userData && (userData.is_staff || isAppAdmin(userData.groups))) {
    return (decompression && !loading) ? renderData() : <Progress />;
  }

  return <PlaceHolderText content="No permissions to view this page" height={height} />;
};

export default InterfaceDecompressions;
